/** @jsx jsx */
import { forwardRef, type PropsWithChildren } from 'react';
import { jsx } from '@compiled/react';
import ExpandIcon from '@atlaskit/icon/core/chevron-down';

import { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';

import { LinkItem } from '@atlaskit/menu';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { DeepDiveInfo, Project } from '../../../common/types.tsx';
import messages from './messages.tsx';

type BoardsDropdownItemProps = DeepDiveInfo & { parent?: string; project: Project };

const BoardDropdownItemComponentLink = forwardRef<HTMLAnchorElement, PropsWithChildren<{}>>(
	({ children, ...props }, ref) => (
		// This was added as part of BLU-8308 to fix required children a11y violation
		<div role="menuitem">
			<LinkItem
				{...props}
				ref={ref}
				testId="deep-dive-cards-section.ui.deep-dive-card.dropdown-content.deep-dive-card-content-dropdown-content-item-link"
			>
				{children}
			</LinkItem>
		</div>
	),
);

const BoardDropdownItemComponent = ({
	project,
	content,
	href,
	type,
	children,
	parent,
}: BoardsDropdownItemProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleOnClick = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
		});
		fireUIAnalytics(analyticsEvent, 'deepDiveCardLinkBoard', {
			projectType: project.projectType,
			boardType: type,
			nextGen: project.isSimplified,
		});
	};

	return (children?.length ?? 0) > 0 ? (
		<Box xcss={dropdownItemGroupStyles}>
			<Flex
				aria-level={2}
				xcss={dropdownItemGroupHeadingStyles}
				alignItems="center"
				gap="space.025"
			>
				{content} <ExpandIcon spacing="compact" label="" size="small" />
			</Flex>
			{children?.map((props, index) => (
				<BoardDropdownItemComponent {...props} project={project} key={index} parent={content} />
			))}
		</Box>
	) : (
		<DropdownItem
			testId="deep-dive-cards-section.ui.deep-dive-card.dropdown-content.deep-dive-card-content-dropdown-content"
			href={href}
			onClick={handleOnClick}
			// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
			component={BoardDropdownItemComponentLink}
			{...(parent && { 'aria-description': formatMessage(messages.parentGroupLabel, { parent }) })}
		>
			<Box as="span" xcss={dropdownItemTitleStyles} padding="space.050">
				{content}
			</Box>
		</DropdownItem>
	);
};

type DropdownMenuContentProps = {
	data: DeepDiveInfo[];
	project: Project;
};

export const DropdownMenuContent = ({ data, project }: DropdownMenuContentProps): JSX.Element => {
	return (
		<Box xcss={dropdownMenuHeightStyles}>
			<DropdownItemGroup>
				{data.map((props, index) => (
					<BoardDropdownItemComponent {...props} project={project} key={index} />
				))}
			</DropdownItemGroup>
		</Box>
	);
};

const dropdownMenuHeightStyles = xcss({
	maxHeight: '400px',
});

const dropdownItemGroupStyles = xcss({
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
});

const dropdownItemGroupHeadingStyles = xcss({
	font: token('font.heading.xxsmall'),
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

const dropdownItemTitleStyles = xcss({
	font: token('font.body.small'),
});
